import { sql } from "drizzle-orm";
import {
  type AnySQLiteColumn,
  check,
  index,
  integer,
  primaryKey,
  real,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { activity } from "@/db/schema/activities";
import { activityInstance } from "@/db/schema/activity-instance";
import { user } from "@/db/schema/auth";
import type { MediaData } from "@/db/schema/hiive";
import { hiive } from "@/db/schema/hiive";
import { mvActivity } from "@/db/schema/movement";
import { hiivePack } from "@/db/schema/pack";
import type { Gender } from "@/db/schema/profile";
import { taxProfile } from "@/db/schema/tax";
import { hiiveTier } from "@/db/schema/tier";
import type { PaymentCollection } from "@/lib/booking-collection";
import type { SocialLinks } from "@/lib/contact-links";
import type { TicketQuestion } from "@/lib/events/ticket-questions";
import type { MusicAttachment } from "@/lib/music/attachment";
import type { PricingPolicy } from "@/lib/pricing/types";

export const EVENT_STATUS = [
  "pending_approval",
  "active",
  "inactive",
  // Called off by the organiser: every date cancelled and every booking
  // refunded. Distinct from `archived`, which is a filing decision and says
  // nothing to a member about money. The column has no CHECK constraint, so
  // this list is the only definition of the set.
  "canceled",
  "archived",
  "rejected",
  // A preview link's sandbox copy (see `preview_of_event_id`). Bookable like
  // `active`, but every public list filters on `active`, so it is never
  // discovered — it is reached only through `/preview/<token>`.
  "preview",
] as const;
export type EventStatus = (typeof EVENT_STATUS)[number];

export const EVENT_PATTERN = [
  "single",
  "multi_date_single_slot",
  "multi_date_multi_slot",
] as const;

// Where the event is discoverable, independent of where it physically happens.
// "venue" = only near the venue, "areas" = venue plus the
// eventVisibilityArea rows, "everywhere" = shown regardless of viewer location.
export const EVENT_DISCOVERY_SCOPE = ["venue", "areas", "everywhere"] as const;
export type EventDiscoveryScope = (typeof EVENT_DISCOVERY_SCOPE)[number];

export const EVENT_PAYMENT_PROVIDER = ["razorpay"] as const;

export type EventPattern = (typeof EVENT_PATTERN)[number];
export type EventPaymentProvider = (typeof EVENT_PAYMENT_PROVIDER)[number];

export type EventListItem = {
  title: string;
  description?: string | null;
};

export const EVENT_TIMELINE_ITEM_KIND = [
  "gates_open",
  "start",
  "end",
  "custom",
] as const;

export type EventTimelineItemKind = (typeof EVENT_TIMELINE_ITEM_KIND)[number];

export const EVENT_ATTENDEE_LIST_VISIBILITY = [
  "public",
  "members",
  "tiers",
  "attendees",
  "count_only",
  "hidden",
] as const;

export type EventAttendeeListVisibility =
  (typeof EVENT_ATTENDEE_LIST_VISIBILITY)[number];

/**
 * What the event page says once nobody can book any more, chosen by whoever
 * runs the event.
 *
 * The default (NULL) is the platform's own sentence, which is accurate and
 * useless: "Bookings for this event are currently closed." tells a visitor
 * what the database thinks and nothing about what to do next. A closed
 * booking window means very different things — a class that filled up, a
 * gig you can still walk into, a season that ends and starts again, an event
 * that has simply happened — and only the organiser knows which.
 *
 * Each preset also decides whether the event keeps its place in discovery
 * after the window shuts (see `lib/events/booking-closed-cta.ts`): a closed
 * event with nothing to say is a dead end in the /hiives rail, but one whose
 * owner is saying "just turn up" is the opposite.
 */
/**
 * What a waitlist does when a seat opens.
 *
 * "notify" (default): alert the people in line and leave the seat open — the
 * first to book gets it, and nobody who wants to buy right now is blocked by a
 * seat held for someone who may never come back. Missing an alert never costs
 * a person their place.
 *
 * "hold": the traditional model — reserve the seat for the next person for a
 * fixed window, then move on to the next one.
 */
export const EVENT_WAITLIST_MODE = ["notify", "hold"] as const;
export type EventWaitlistMode = (typeof EVENT_WAITLIST_MODE)[number];

/** The two sides a balanced ticket keeps level. "other" is never constrained. */
export const BALANCE_SIDES = ["male", "female"] as const;
export type BalanceSide = (typeof BALANCE_SIDES)[number];

/**
 * A host's live adjustments to a balanced ticket on one date: extra seats let
 * in on one side, or the ratio switched off. Never shown as numbers to tune —
 * written by the "Let 5 more in" / "Open to everyone" buttons.
 */
export type BalanceOverrides = {
  extra?: Partial<Record<BalanceSide, number>>;
  openToAll?: boolean;
};

export const APPROVAL_PAYMENT_TIMING = ["after_approval", "hold"] as const;
export type ApprovalPaymentTiming = (typeof APPROVAL_PAYMENT_TIMING)[number];
export const MIN_HOLD_HOURS = 24;
export const MAX_HOLD_HOURS = 72;

/**
 * How a balanced ticket fills.
 * - "pace": a side that gets ahead waits for the other to catch up, so the
 *   room is level all the way through (the original behaviour).
 * - "cap": each side simply has its share of the seats and books freely up to
 *   it; nobody waits for the other side. The default for approval tickets,
 *   where the host has already picked who comes.
 */
export const GENDER_BALANCE_STYLE = ["pace", "cap"] as const;
export type GenderBalanceStyle = (typeof GENDER_BALANCE_STYLE)[number];

/**
 * The host's split of a balanced ticket's seats, e.g. 14 women and 10 men of
 * 24. Stored as the numbers the host typed; a date with a different capacity
 * keeps the same proportion. Null = half and half.
 */
export type GenderBalanceSplit = Record<BalanceSide, number>;

/**
 * When the waitlist head start for a side (or the whole ticket, "any") ends,
 * as epoch ms. While it runs, only people already in line may book.
 */
export type WaitlistPriority = Partial<
  Record<BalanceSide | "other" | "any", number>
>;

export const EVENT_BOOKING_CLOSED_CTA = [
  "walk_ins",
  "sold_out",
  "reopening",
  "finished",
] as const;

export type EventBookingClosedCta = (typeof EVENT_BOOKING_CLOSED_CTA)[number];

export const event = sqliteTable(
  "event",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),

    description: text("description").notNull(),
    bannerMedia: text("banner_media", { mode: "json" })
      .notNull()
      .$type<MediaData[]>(),
    /**
     * Optional song for a STATIC banner: plays Instagram-style over the image
     * (a video banner keeps its own sound). Server-resolved snapshot.
     */
    bannerMusic: text("banner_music", {
      mode: "json",
    }).$type<MusicAttachment | null>(),

    venueName: text("venue_name").notNull(),
    address: text("address").notNull(),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    geohash: text("geohash").notNull(),

    discoveryScope: text("discovery_scope")
      .notNull()
      .$type<EventDiscoveryScope>()
      .default("venue"),

    /**
     * Withhold the pin, keep the matching.
     *
     * An invite-only event still has to be findable — discovery is geohash
     * based, so the coordinates cannot simply be dropped or the event vanishes
     * from "near you". This separates the two uses: `latitude`/`longitude`
     * still drive proximity, but the map, the exact address and the JSON-LD
     * `GeoCoordinates` are withheld from the public page. Without it an
     * organiser who typed a deliberately vague address still handed Google the
     * precise location in structured data.
     */
    hideExactLocation: integer("hide_exact_location", { mode: "boolean" })
      .notNull()
      .default(false),

    /**
     * Render "8 PM onwards" instead of "8 PM – 3 AM".
     *
     * Display only. `event_occurrence.endTime` stays required and real: booking
     * windows, the live-event card and every "has this finished" check read it,
     * and making it nullable to express an open end would push that ambiguity
     * into all of them.
     */
    openEndedTime: integer("open_ended_time", { mode: "boolean" })
      .notNull()
      .default(false),

    pattern: text("pattern").notNull().$type<EventPattern>().default("single"),
    paymentProvider: text("payment_provider")
      .notNull()
      .$type<EventPaymentProvider>()
      .default("razorpay"),

    // Active refund policy for this event. Deliberately not a FK — refundPolicy
    // rows are immutable, and a FK here would close an import cycle through
    // payout.ts. NULL falls back to the owning hiive's default.
    refundPolicyId: integer("refund_policy_id"),

    // Queen-created events require platform-admin approval before they go live.
    // Mirrors venue.status — admins can create directly into "active".
    status: text("status")
      .notNull()
      .$type<EventStatus>()
      .default("pending_approval"),

    highlightStandsOut: text("highlight_stands_out"),
    highlightExperience: text("highlight_experience"),
    highlightSpecialAttractions: text("highlight_special_attractions"),
    highlightPerfectFor: text("highlight_perfect_for"),
    allowedGenders: text("allowed_genders", { mode: "json" })
      .notNull()
      .$type<Gender[]>()
      .default(sql`'[]'`),
    attendeeListVisibility: text("attendee_list_visibility")
      .notNull()
      .$type<EventAttendeeListVisibility>()
      .default("public"),
    /** See EVENT_BOOKING_CLOSED_CTA. NULL keeps the platform default copy. */
    bookingClosedCta: text("booking_closed_cta").$type<EventBookingClosedCta>(),
    /**
     * One optional line under the preset headline, in the organiser's own
     * words ("Cash only at the door", "Next term opens 1 March"). Moderated
     * like any other public event text.
     */
    bookingClosedMessage: text("booking_closed_message"),
    /**
     * May a booker assign a seat to someone with no Hiive account, by name and
     * email?
     *
     * Default ON, because the common case is booking for friends who are not on
     * Hiive. An owner turns it off where every attendee must hold an account —
     * waiver flows, member-only clubs — and it doubles as the per-hiive kill
     * switch. Note that tier- and gender-restricted events refuse guests
     * regardless of this flag: there is no way to prove a tier or a gender for
     * someone with no profile.
     */
    guestAttendeesEnabled: integer("guest_attendees_enabled", {
      mode: "boolean",
    })
      .notNull()
      .default(true),

    /**
     * Only holders of this pack may book — the studio's own students, not the
     * street.
     *
     * Distinct from tier access, which gates on MEMBERSHIP of the hiive. This
     * gates on having bought one particular thing, and the two are not the same
     * question: Salsa Nova's monthly Practice & Makeup session is for the
     * people on the Full Batch Pass that month, and making them a membership
     * tier to say so would invent a second, permanent identity for a
     * four-week course.
     *
     * Null is the default and means public. See
     * 20260915170000_event_pack_exclusive.
     */
    packExclusivePackId: integer("pack_exclusive_pack_id").references(
      () => hiivePack.id,
      { onDelete: "set null" },
    ),
    // Public contact details for this event specifically — usually copied
    // from the owning hiive. See lib/contact-links.ts.
    contactPhone: text("contact_phone"),
    contactEmail: text("contact_email"),
    websiteUrl: text("website_url"),
    socialLinks: text("social_links", { mode: "json" })
      .$type<SocialLinks>()
      .notNull()
      .default(sql`'{}'`),
    thingsToKnow: text("things_to_know", { mode: "json" })
      .notNull()
      .$type<string[]>()
      .default(sql`'[]'`),
    amenities: text("amenities", { mode: "json" })
      .notNull()
      .$type<string[]>()
      .default(sql`'[]'`),
    termsAndConditions: text("terms_and_conditions", { mode: "json" })
      .notNull()
      .$type<EventListItem[]>()
      .default(sql`'[]'`),
    faqs: text("faqs", { mode: "json" })
      .notNull()
      .$type<EventListItem[]>()
      .default(sql`'[]'`),
    autoCheckInOnScan: integer("auto_check_in_on_scan", { mode: "boolean" })
      .notNull()
      .default(true),
    eventChatEnabled: integer("event_chat_enabled", { mode: "boolean" })
      .notNull()
      .default(true),
    // When true, checked-in attendee story contributions publish immediately
    // instead of waiting in the queen's approval queue (Phase D).
    storyAutoAcceptContributions: integer("story_auto_accept_contributions", {
      mode: "boolean",
    })
      .notNull()
      .default(false),

    // Holds approval audit details (rejectionReason, rejectedAt, rejectedBy).
    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    /**
     * Set only on a preview link's sandbox copy: the real event it was copied
     * from. The copy carries its own dates, tickets and bookings so somebody
     * walking the flow never touches the real event's seats, guest list or
     * sales. Organiser and admin lists filter these out. See
     * lib/events/preview/sandbox.ts.
     */
    previewOfEventId: integer("preview_of_event_id").references(
      (): AnySQLiteColumn => event.id,
      { onDelete: "cascade" },
    ),

    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_slug_per_hiive_unique").on(table.hiiveId, table.slug),
    index("event_by_preview_of").on(table.previewOfEventId),
    index("event_by_hiive").on(table.hiiveId),
    index("event_by_pattern").on(table.pattern),
    index("event_by_status").on(table.status),
    index("event_geohash_idx").on(table.geohash),
    index("event_by_discovery_scope").on(table.discoveryScope),
  ],
);

/**
 * `pending`  — invited by the host; visible only to the two hiives' queens.
 * `accepted` — credited publicly, listed on the partner's page, reviewable.
 * `declined` — the partner said no; the host may re-invite later.
 * `left`     — the partner stepped away after accepting.
 * A host removing a partner deletes the row outright.
 */
export const EVENT_COLLABORATOR_STATUS = [
  "pending",
  "accepted",
  "declined",
  "left",
] as const;
export type EventCollaboratorStatus =
  (typeof EVENT_COLLABORATOR_STATUS)[number];
export { MAX_EVENT_COLLABORATORS } from "@/lib/events/collaboration-limits";

/**
 * Other Hiives credited on an event; the owning Hiive remains event.hiiveId.
 * Nothing is public until the partner accepts — like an Instagram Collab, a
 * host cannot claim a partnership on its own.
 */
export const eventCollaborator = sqliteTable(
  "event_collaborator",
  {
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),
    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").notNull().default(0),
    status: text("status")
      .notNull()
      .$type<EventCollaboratorStatus>()
      .default("pending"),
    invitedByUserId: text("invited_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    invitedAt: integer("invited_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
    respondedByUserId: text("responded_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    respondedAt: integer("responded_at", { mode: "timestamp" }),
    /** Last time this partner pushed the event to its own members. */
    announcedAt: integer("announced_at", { mode: "timestamp" }),
    announcedRecipientCount: integer("announced_recipient_count")
      .notNull()
      .default(0),
  },
  (table) => [
    primaryKey({ columns: [table.eventId, table.hiiveId] }),
    index("event_collaborator_hiive_idx").on(
      table.hiiveId,
      table.status,
      table.eventId,
    ),
  ],
);

// Extra areas where an "areas"-scoped event is discoverable (e.g. an event
// physically in Bali that should surface for viewers browsing Mumbai).
export const eventVisibilityArea = sqliteTable(
  "event_visibility_area",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),

    label: text("label").notNull(),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    geohash: text("geohash").notNull(),
    // Optional per-area reach override; discovery falls back to its defaults.
    radiusKm: integer("radius_km"),
    sortOrder: integer("sort_order").notNull().default(0),

    ...timestamps,
  },
  (table) => [
    index("event_visibility_area_by_event").on(table.eventId),
    index("event_visibility_area_geohash_idx").on(table.geohash),
  ],
);

export type EventVisibilityArea = typeof eventVisibilityArea.$inferSelect;
export type EventVisibilityAreaInsert = typeof eventVisibilityArea.$inferInsert;

export type Event = typeof event.$inferSelect;
export type EventInsert = typeof event.$inferInsert;

export type EventCreateDraftValues = Record<string, unknown>;

export const eventCreateDraft = sqliteTable(
  "event_create_draft",
  {
    hiiveId: integer("hiive_id")
      .primaryKey()
      .references(() => hiive.id, { onDelete: "cascade" }),
    draftValues: text("draft_values", { mode: "json" })
      .notNull()
      .$type<EventCreateDraftValues>(),
    version: integer("version").notNull().default(1),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    updatedByUserId: text("updated_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    ...timestamps,
  },
  (table) => [
    index("event_create_draft_by_hiive_updated").on(
      table.hiiveId,
      table.updatedAt,
    ),
  ],
);

/**
 * Weekly recurring class slot — the "template" behind a studio timetable.
 *
 * A rule says "this event runs every Tuesday 08:00–09:00 in Asia/Kolkata".
 * `eventOccurrence` rows are still the only bookable thing; a background
 * materializer walks each active rule forward and creates the concrete
 * occurrences on a rolling horizon, so an open-ended timetable never runs out
 * of bookable sessions the way a one-shot "generate sessions over this date
 * range" pass does.
 *
 * Shape deliberately mirrors `venueExperienceFixedSlot` (dayOfWeek 0=Sun..6=Sat,
 * minutes since local midnight, `effective*` window) so the two scheduling
 * surfaces stay legible side by side. `timeZone` lives on the rule rather than
 * the event because the wall-clock time is the thing being repeated: 08:00 must
 * stay 08:00 across a DST boundary, which storing a UTC offset cannot express.
 *
 * Editing a published timetable is modelled as supersession rather than
 * mutation — close the old rule with `effectiveUntil` and insert a replacement
 * whose `effectiveFrom` is the changeover date. Occurrences already generated
 * (and possibly booked) before the changeover keep their provenance and are
 * left alone.
 */
export const eventRecurrenceRule = sqliteTable(
  "event_recurrence_rule",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),

    // 0 = Sunday .. 6 = Saturday (matches venueExperienceFixedSlot).
    dayOfWeek: integer("day_of_week").notNull(),
    // Minutes since local midnight in `timeZone`.
    startMinute: integer("start_minute").notNull(),
    durationMinutes: integer("duration_minutes").notNull(),
    timeZone: text("time_zone").notNull().default("Asia/Kolkata"),

    slotLabel: text("slot_label"),
    // NULL falls back to each pass type's defaultCapacity.
    capacity: integer("capacity"),

    bookingOpensHoursBefore: integer("booking_opens_hours_before"),
    bookingClosesHoursBefore: integer("booking_closes_hours_before"),

    // Local calendar dates (YYYY-MM-DD) in `timeZone`. A NULL
    // `effectiveUntil` is an open-ended weekly slot that keeps rolling.
    effectiveFrom: text("effective_from").notNull(),
    effectiveUntil: text("effective_until"),

    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),

    // Last local date this rule has been expanded through. The materializer
    // resumes from here instead of re-scanning the whole history.
    materializedThrough: text("materialized_through"),

    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    updatedByUserId: text("updated_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    ...timestamps,
  },
  (table) => [
    index("event_recurrence_rule_by_event").on(table.eventId),
    index("event_recurrence_rule_by_slot").on(
      table.eventId,
      table.dayOfWeek,
      table.startMinute,
    ),
    index("event_recurrence_rule_active").on(table.isActive, table.dayOfWeek),
    uniqueIndex("event_recurrence_rule_unique_slot").on(
      table.eventId,
      table.dayOfWeek,
      table.startMinute,
      table.effectiveFrom,
    ),
    check(
      "event_recurrence_rule_day_check",
      sql`${table.dayOfWeek} >= 0 and ${table.dayOfWeek} <= 6`,
    ),
    check(
      "event_recurrence_rule_start_check",
      sql`${table.startMinute} >= 0 and ${table.startMinute} <= 1439`,
    ),
    check(
      "event_recurrence_rule_duration_check",
      sql`${table.durationMinutes} > 0 and ${table.durationMinutes} <= 1440`,
    ),
  ],
);

export type EventRecurrenceRule = typeof eventRecurrenceRule.$inferSelect;
export type EventRecurrenceRuleInsert = typeof eventRecurrenceRule.$inferInsert;

/**
 * Singleton bookkeeping for the self-rescheduling materializer chain
 * (mirrors `storyGcState`).
 */
export const eventRecurrenceState = sqliteTable("event_recurrence_state", {
  id: integer("id").primaryKey(),
  workflowRunId: text("workflow_run_id"),
  nextRunAt: integer("next_run_at", { mode: "timestamp" }),
  lastRunAt: integer("last_run_at", { mode: "timestamp" }),
  lastCreatedCount: integer("last_created_count").notNull().default(0),
  ...timestamps,
});

export const EVENT_OCCURRENCE_STATUS = [
  "scheduled",
  "completed",
  "canceled",
] as const;

export type EventOccurrenceStatus = (typeof EVENT_OCCURRENCE_STATUS)[number];

export const EVENT_OCCURRENCE_JOB_TYPE = [
  "reminder_30m",
  "close_occurrence",
  "close_join_requests",
  "lock_chat_48h",
  "archive_chat_7d",
  "review_request_1h",
  "connect_prompt_12h",
  "story_recap_24h",
  // One push before the earliest add-on pre-order deadline of the session.
  "addon_last_call",
] as const;

export type EventOccurrenceJobType = (typeof EVENT_OCCURRENCE_JOB_TYPE)[number];

export const EVENT_OCCURRENCE_JOB_STATUS = [
  "scheduled",
  "canceled",
  "completed",
  "failed",
] as const;

export type EventOccurrenceJobStatus =
  (typeof EVENT_OCCURRENCE_JOB_STATUS)[number];

export const eventOccurrence = sqliteTable(
  "event_occurrence",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),

    // Set when this occurrence was generated from a weekly recurrence rule.
    // NULL = a one-off session, which the materializer never touches. Kept as
    // SET NULL so deleting a rule downgrades its future sessions to one-offs
    // rather than cascading away rows that may already hold bookings.
    recurrenceRuleId: integer("recurrence_rule_id").references(
      (): AnySQLiteColumn => eventRecurrenceRule.id,
      { onDelete: "set null" },
    ),

    startTime: integer("start_time", { mode: "timestamp" }).notNull(),
    endTime: integer("end_time", { mode: "timestamp" }).notNull(),
    slotLabel: text("slot_label"),

    capacity: integer("capacity").notNull().default(0),

    status: text("status")
      .notNull()
      .$type<EventOccurrenceStatus>()
      .default("scheduled"),

    bookingOpensAt: integer("booking_opens_at", { mode: "timestamp" }),
    bookingClosesAt: integer("booking_closes_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (table) => [
    index("event_occurrence_by_event").on(table.eventId),
    index("event_occurrence_by_start").on(table.startTime),
    index("event_occurrence_by_rule").on(
      table.recurrenceRuleId,
      table.startTime,
    ),
    uniqueIndex("event_occurrence_unique_window").on(
      table.eventId,
      table.startTime,
      table.endTime,
    ),
  ],
);

export const eventOccurrenceJob = sqliteTable(
  "event_occurrence_job",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    occurrenceId: integer("occurrence_id")
      .notNull()
      .references(() => eventOccurrence.id, { onDelete: "cascade" }),
    type: text("type").notNull().$type<EventOccurrenceJobType>(),
    status: text("status")
      .notNull()
      .$type<EventOccurrenceJobStatus>()
      .default("scheduled"),
    workflowJobId: text("workflow_job_id"),
    scheduledFor: integer("scheduled_for", { mode: "timestamp" }).notNull(),
    canceledAt: integer("canceled_at", { mode: "timestamp" }),
    completedAt: integer("completed_at", { mode: "timestamp" }),
    lastError: text("last_error"),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_occurrence_job_unique").on(
      table.occurrenceId,
      table.type,
    ),
    index("event_occurrence_job_status").on(table.status),
    index("event_occurrence_job_occurrence").on(table.occurrenceId),
  ],
);

export const eventOccurrenceTimelineItem = sqliteTable(
  "event_occurrence_timeline_item",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    occurrenceId: integer("occurrence_id")
      .notNull()
      .references(() => eventOccurrence.id, { onDelete: "cascade" }),
    kind: text("kind")
      .notNull()
      .$type<EventTimelineItemKind>()
      .default("custom"),
    label: text("label").notNull(),
    description: text("description"),
    occursAt: integer("occurs_at", { mode: "timestamp" }).notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    ...timestamps,
  },
  (table) => [
    index("event_occurrence_timeline_item_occurrence").on(table.occurrenceId),
    index("event_occurrence_timeline_item_sort").on(
      table.occurrenceId,
      table.sortOrder,
      table.occursAt,
    ),
  ],
);

export const eventPassType = sqliteTable(
  "event_pass_type",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description"),

    paymentCollection: text("payment_collection")
      .notNull()
      .$type<PaymentCollection>()
      .default("online"),

    attendeesPerPass: integer("attendees_per_pass").notNull().default(1),
    defaultCapacity: integer("default_capacity").notNull().default(0),
    priceAmountMinor: integer("price_amount_minor").notNull().default(0),
    // When true, priceAmountMinor is a deposit charged at booking; the
    // remaining balance (totalPriceMinor - priceAmountMinor) is collected by
    // the organizer directly, outside the platform.
    isDeposit: integer("is_deposit", { mode: "boolean" })
      .notNull()
      .default(false),
    totalPriceMinor: integer("total_price_minor"),
    currency: text("currency").notNull().default("INR"),

    // Tax. `priceAmountMinor` above stays the GROSS the member sees and pays —
    // a null taxProfileId means untaxed, which is every row predating this.
    // `priceIncludesTax` describes the stored column, which is gross either
    // way; the organiser's *entry* mode (typing 1800 + 18% vs typing 2124) is
    // resolved before the write. See src/lib/tax/engine.ts.
    pricingPolicy: text("pricing_policy", {
      mode: "json",
    }).$type<PricingPolicy | null>(),
    taxProfileId: integer("tax_profile_id").references(() => taxProfile.id, {
      onDelete: "set null",
    }),
    priceIncludesTax: integer("price_includes_tax", { mode: "boolean" })
      .notNull()
      .default(true),

    waitlistEnabled: integer("waitlist_enabled", { mode: "boolean" })
      .notNull()
      .default(false),
    waitlistMode: text("waitlist_mode")
      .notNull()
      .$type<EventWaitlistMode>()
      .default("notify"),
    /** Give people already in line ten minutes before a freed seat goes public. */
    waitlistHeadStart: integer("waitlist_head_start", { mode: "boolean" })
      .notNull()
      .default(false),

    /**
     * Keep men and women balanced on this ticket. Hosts choose the style and,
     * optionally, the split; every number behind pacing (opening batch, how
     * far one side may lead) is derived from capacity in
     * lib/events/gender-balance.ts.
     */
    genderBalance: integer("gender_balance", { mode: "boolean" })
      .notNull()
      .default(false),
    genderBalanceStyle: text("gender_balance_style")
      .notNull()
      .$type<GenderBalanceStyle>()
      .default("pace"),
    genderBalanceSplit: text("gender_balance_split", {
      mode: "json",
    }).$type<GenderBalanceSplit | null>(),
    /** Drop the ratio in the last 24 hours so no seat goes empty. */
    genderBalanceLateFill: integer("gender_balance_late_fill", {
      mode: "boolean",
    })
      .notNull()
      .default(true),

    inviteOnly: integer("invite_only", { mode: "boolean" })
      .notNull()
      .default(false),

    /**
     * People request to join and the host approves them. Approval lets them
     * book; it never holds a seat, so whoever pays first gets it.
     */
    requiresApproval: integer("requires_approval", { mode: "boolean" })
      .notNull()
      .default(false),
    /**
     * On a paid approval ticket: "after_approval" (approved people check out,
     * first to pay wins) or "hold" (the card is held when someone requests and
     * charged when the host approves). Holds are card-only.
     */
    approvalPaymentTiming: text("approval_payment_timing")
      .notNull()
      .$type<ApprovalPaymentTiming>()
      .default("after_approval"),
    /** How long a card hold waits for the host, 24–72 hours. */
    holdHours: integer("hold_hours").notNull().default(72),

    /** Extra checkout questions for this ticket; see lib/events/ticket-questions. */
    bookingQuestions: text("booking_questions", { mode: "json" }).$type<
      TicketQuestion[] | null
    >(),

    // Hidden from public ticket lists; invite links and guest-list checks stay active.
    hideFromNonGuests: integer("hide_from_non_guests", { mode: "boolean" })
      .notNull()
      .default(false),

    linkOnly: integer("link_only", { mode: "boolean" })
      .notNull()
      .default(false),

    sortOrder: integer("sort_order").notNull().default(0),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    maxPerBooking: integer("max_per_booking"),

    metadata: text("metadata"),

    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_pass_slug_unique").on(table.eventId, table.slug),
    index("event_pass_by_event").on(table.eventId),
    index("event_pass_sort").on(table.eventId, table.sortOrder),
  ],
);

/** One verified contact route is sufficient to claim an invite-only ticket. */
export const eventPassGuestList = sqliteTable(
  "event_pass_guest_list",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    passTypeId: integer("pass_type_id")
      .notNull()
      .references(() => eventPassType.id, { onDelete: "cascade" }),
    phoneNumber: text("phone_number"),
    email: text("email"),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    ...timestamps,
  },
  (table) => [
    check(
      "event_pass_guest_list_contact_check",
      sql`(${table.phoneNumber} IS NOT NULL) != (${table.email} IS NOT NULL)`,
    ),
    uniqueIndex("event_pass_guest_list_phone_unique").on(
      table.passTypeId,
      table.phoneNumber,
    ),
    uniqueIndex("event_pass_guest_list_email_unique").on(
      table.passTypeId,
      table.email,
    ),
    index("event_pass_guest_list_pass_idx").on(table.passTypeId),
  ],
);

/**
 * A shareable link that puts whoever opens it on an invite-only ticket's guest
 * list. Possession of the token is the invitation, so it is long, random and
 * revocable, and `maxClaims` caps how far a forwarded link can travel.
 */
export const eventPassInviteLink = sqliteTable(
  "event_pass_invite_link",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    passTypeId: integer("pass_type_id")
      .notNull()
      .references(() => eventPassType.id, { onDelete: "cascade" }),
    token: text("token").notNull(),
    label: text("label"),
    maxClaims: integer("max_claims"),
    revokedAt: integer("revoked_at", { mode: "timestamp" }),
    createdByUserId: text("created_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_pass_invite_link_token_unique").on(table.token),
    index("event_pass_invite_link_pass_idx").on(table.passTypeId),
  ],
);

/**
 * An account that accepted an invite link. Counts as being on the guest list
 * for that ticket, alongside the organiser's phone and email entries.
 */
export const eventPassInviteClaim = sqliteTable(
  "event_pass_invite_claim",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    passTypeId: integer("pass_type_id")
      .notNull()
      .references(() => eventPassType.id, { onDelete: "cascade" }),
    linkId: integer("link_id").references(() => eventPassInviteLink.id, {
      onDelete: "set null",
    }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_pass_invite_claim_user_unique").on(
      table.passTypeId,
      table.userId,
    ),
    index("event_pass_invite_claim_link_idx").on(table.linkId),
    index("event_pass_invite_claim_user_idx").on(table.userId),
  ],
);

export const eventPassPriceRevision = sqliteTable(
  "event_pass_price_revision",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    passTypeId: integer("pass_type_id")
      .notNull()
      .references(() => eventPassType.id, { onDelete: "cascade" }),
    changedByUserId: text("changed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    previousPriceAmountMinor: integer("previous_price_amount_minor").notNull(),
    priceAmountMinor: integer("price_amount_minor").notNull(),
    previousIsDeposit: integer("previous_is_deposit", { mode: "boolean" })
      .notNull()
      .default(false),
    isDeposit: integer("is_deposit", { mode: "boolean" })
      .notNull()
      .default(false),
    previousTotalPriceMinor: integer("previous_total_price_minor"),
    totalPriceMinor: integer("total_price_minor"),
    currency: text("currency").notNull().default("INR"),

    ...timestamps,
  },
  (table) => [
    index("event_pass_price_revision_by_pass").on(table.passTypeId),
    index("event_pass_price_revision_by_user").on(table.changedByUserId),
    index("event_pass_price_revision_created").on(table.createdAt),
  ],
);

export const eventOccurrencePass = sqliteTable(
  "event_occurrence_pass",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    occurrenceId: integer("occurrence_id")
      .notNull()
      .references(() => eventOccurrence.id, { onDelete: "cascade" }),
    passTypeId: integer("pass_type_id")
      .notNull()
      .references(() => eventPassType.id, { onDelete: "cascade" }),

    priceAmountMinor: integer("price_amount_minor"),
    capacity: integer("capacity"),
    waitlistEnabled: integer("waitlist_enabled", { mode: "boolean" })
      .notNull()
      .default(false),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    balanceOverrides: text("balance_overrides", {
      mode: "json",
    }).$type<BalanceOverrides | null>(),
    waitlistPriority: text("waitlist_priority", {
      mode: "json",
    }).$type<WaitlistPriority | null>(),

    saleStartsAt: integer("sale_starts_at", { mode: "timestamp" }),
    saleEndsAt: integer("sale_ends_at", { mode: "timestamp" }),

    metadata: text("metadata"),

    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_occurrence_pass_unique").on(
      table.occurrenceId,
      table.passTypeId,
    ),
    index("event_occurrence_pass_by_occurrence").on(table.occurrenceId),
  ],
);

export const eventTierAccess = sqliteTable(
  "event_tier_access",
  {
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),
    tierId: integer("tier_id")
      .notNull()
      .references(() => hiiveTier.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "event_tier_access_pk",
      columns: [table.eventId, table.tierId],
    }),
  ],
);

export const eventAttendeeListTierAccess = sqliteTable(
  "event_attendee_list_tier_access",
  {
    eventId: integer("event_id")
      .notNull()
      .references(() => event.id, { onDelete: "cascade" }),
    tierId: integer("tier_id")
      .notNull()
      .references(() => hiiveTier.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "event_attendee_list_tier_access_pk",
      columns: [table.eventId, table.tierId],
    }),
  ],
);

export const eventPassTierPrice = sqliteTable(
  "event_pass_tier_price",
  {
    passTypeId: integer("pass_type_id")
      .notNull()
      .references(() => eventPassType.id, { onDelete: "cascade" }),
    tierId: integer("tier_id")
      .notNull()
      .references(() => hiiveTier.id, { onDelete: "cascade" }),
    priceAmountMinor: integer("price_amount_minor").notNull().default(0),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "event_pass_tier_price_pk",
      columns: [table.passTypeId, table.tierId],
    }),
    index("event_pass_tier_price_by_pass").on(table.passTypeId),
    index("event_pass_tier_price_by_tier").on(table.tierId),
  ],
);

export const eventActivity = sqliteTable(
  "event_activity",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    eventId: integer("event_id")
      .notNull()
      .references((): AnySQLiteColumn => event.id, { onDelete: "cascade" }),
    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_activity_unique").on(table.eventId, table.activityId),
    index("event_activity_by_event").on(table.eventId),
  ],
);

export const eventActivityCompletion = sqliteTable(
  "event_activity_completion",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    occurrenceId: integer("occurrence_id")
      .notNull()
      .references(() => eventOccurrence.id, { onDelete: "cascade" }),
    eventActivityId: integer("event_activity_id")
      .notNull()
      .references(() => eventActivity.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    completedAt: integer("completed_at", { mode: "timestamp" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_activity_completion_unique").on(
      table.occurrenceId,
      table.eventActivityId,
      table.userId,
    ),
    index("event_activity_completion_by_occurrence").on(table.occurrenceId),
  ],
);

export const eventActivityCompletionMovement = sqliteTable(
  "event_activity_completion_movement",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    completionId: integer("completion_id")
      .notNull()
      .references(() => eventActivityCompletion.id, { onDelete: "cascade" }),
    mvActivityId: text("mv_activity_id")
      .notNull()
      .references(() => mvActivity.id, { onDelete: "cascade" }),
    startedAt: integer("started_at", { mode: "timestamp" }),
    durationSeconds: integer("duration_seconds"),
    distanceMeters: real("distance_meters"),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_activity_completion_movement_unique").on(
      table.completionId,
    ),
    uniqueIndex("event_activity_completion_movement_mv_unique").on(
      table.mvActivityId,
    ),
    index("event_activity_completion_movement_by_completion").on(
      table.completionId,
    ),
  ],
);

export type EventActivity = typeof eventActivity.$inferSelect;
export type EventActivityInsert = typeof eventActivity.$inferInsert;
export type EventActivityCompletion =
  typeof eventActivityCompletion.$inferSelect;
export type EventActivityCompletionInsert =
  typeof eventActivityCompletion.$inferInsert;
export type EventActivityCompletionMovement =
  typeof eventActivityCompletionMovement.$inferSelect;
export type EventActivityCompletionMovementInsert =
  typeof eventActivityCompletionMovement.$inferInsert;
export type EventOccurrenceJob = typeof eventOccurrenceJob.$inferSelect;
export type EventOccurrenceJobInsert = typeof eventOccurrenceJob.$inferInsert;
export type EventOccurrenceTimelineItem =
  typeof eventOccurrenceTimelineItem.$inferSelect;
export type EventOccurrenceTimelineItemInsert =
  typeof eventOccurrenceTimelineItem.$inferInsert;

export const eventOccurrenceActivityCompletion = sqliteTable(
  "event_occurrence_activity_completion",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    occurrenceId: integer("occurrence_id")
      .notNull()
      .references(() => eventOccurrence.id, { onDelete: "cascade" }),
    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id),
    activityInstanceId: integer("activity_instance_id").references(
      () => activityInstance.id,
      { onDelete: "set null" },
    ),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    completedAt: integer("completed_at", { mode: "timestamp" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("event_occurrence_activity_completion_unique").on(
      table.occurrenceId,
      table.activityId,
      table.userId,
    ),
    index("event_occurrence_activity_completion_occurrence").on(
      table.occurrenceId,
    ),
    index("event_occurrence_activity_completion_user").on(table.userId),
    index("event_occurrence_activity_completion_instance").on(
      table.activityInstanceId,
    ),
  ],
);

export type EventOccurrenceActivityCompletion =
  typeof eventOccurrenceActivityCompletion.$inferSelect;
export type EventOccurrenceActivityCompletionInsert =
  typeof eventOccurrenceActivityCompletion.$inferInsert;
